/**
 * Last-Minute Clearance — display formatting only.
 *
 * Every number these helpers touch is a CLOCK HOUR or a lead-time in days, both stamped by the
 * backend (`ResolveClearanceRuleMutation`). Nothing here touches money or a percentage: the rule
 * card renders the worst-case line's prices and discounts exactly as the engine walked them
 * (`ClearancePolicy.worst_case_descent`), and every clearance percentage is positive on both
 * sides of the wire.
 */

/** A whole local hour as an operator would say it: 0 → "midnight", 16 → "4pm". */
export function formatClockHour(hour: number): string {
  if (!Number.isFinite(hour)) return ''
  if (hour === 0) return 'midnight'
  if (hour === 12) return 'midday'
  const suffix = hour < 12 ? 'am' : 'pm'
  return `${hour % 12}${suffix}`
}

/**
 * The stored depth as the engine's own trace prints it: "−10%", with the typographic minus §12
 * locks on every surface. A character substitution, never arithmetic — the number itself is the
 * backend's `clearance_offset_pct`, rendered exactly as stamped (no abs, no rounding, no sign
 * flip), which is the whole reason that column is serialized rather than derived.
 */
export function depthBadgeLabel(pct: number): string {
  return `${String(pct).replace('-', '\u2212')}%`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "20 Aug" / "4 Nov 2025" — the day the rule started running, for the card's "Running since".
 *
 * THE INSTANT ARRIVES ALREADY IN THE PROPERTY'S OWN ZONE (ResolveClearanceRuleMutation serializes
 * it that way), so this reads the calendar date straight off the string and does no timezone
 * arithmetic of its own: a browser in Sydney must not date a London hotel's start to the day
 * after. The year is dropped in the current one, which is how a hotelier writes a date; `today`
 * is injectable so a test can pin it rather than expire.
 *
 * Returns null for anything that is not a date, so the card can render nothing rather than
 * "Running since Invalid Date".
 */
export function runningSinceLabel(iso: string | null | undefined, today: Date = new Date()): string | null {
  if (!iso) return null
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!parts) return null

  const [, year, month, day] = parts
  const name = MONTHS[Number(month) - 1]
  if (!name) return null

  const label = `${Number(day)} ${name}`
  return Number(year) === today.getFullYear() ? label : `${label} ${year}`
}

/** A lead-time as an operator names the day: 0 → "today", 1 → "tomorrow", 2 → "2 days ahead". */
export function leadDayLabel(lead: number): string {
  if (lead === 0) return 'today'
  if (lead === 1) return 'tomorrow'
  return `${lead} days ahead`
}

/**
 * Which arrivals the rule can touch, in days rather than lead-times.
 *
 * The band is the list of lead-times the backend derived from the property's own booking curve, and
 * it does NOT always start at today: a hotel whose last day fills reliably gets a band that skips
 * it. The sentence therefore names both ends rather than assuming it starts now. An empty list is
 * the rule's auto-dormancy (plan §3.5) and gets its own wording on the page.
 */
export function bandSentence(bandLeads: number[]): string {
  if (bandLeads.length === 0) return 'No arrivals — there is no late demand left to chase here'
  const first = Math.min(...bandLeads)
  const last = Math.max(...bandLeads)
  if (first === last) return `Arrivals ${leadDayLabel(first)} only`
  if (bandLeads.length === 2) return `Arrivals ${leadDayLabel(first)} and ${leadDayLabel(last)}`
  return `Arrivals ${leadDayLabel(first)} through ${leadDayLabel(last)}`
}
